import { useState } from 'react';
import { Badge, ConsolePage, DataTable, Icon, Modal, Panel, QR, QueryState, ListSkeleton, Segmented, Td, type Column, type BadgeKind } from '@/components';
import { useToast } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useInvites, useCreateInvite, useRevokeInvite } from '@/hooks/queries';
import { useOrg } from '@/hooks/queries';
import { professions, PROFESSION_IDS } from '@/data/professions';
import { roleDefs } from '@/data/roleDefs';
import type { CreateInviteInput } from '@/repositories/types';
import type { Invite, InviteStatus, Profession, Role } from '@/types/domain';
import { normalizeUnit, type ExpiryCode } from '@/lib/invite';

const STATUS_BADGE: Record<InviteStatus, BadgeKind> = { active: 'safe', used: 'slate', revoked: 'danger', expired: 'gray' };
const COLS: Column[] = [
  { label: 'Invite token' },
  { label: 'Role / Profession' },
  { label: 'Destination' },
  { label: 'Uses', align: 'center' },
  { label: 'Expires' },
  { label: 'Status' },
  { label: '' },
];

const EXPIRY_OPTS: { value: ExpiryCode; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

export function AdminInvites() {
  const { toast } = useToast();
  const { user: actor } = useAuth();
  // Only a superadmin may invite elevated roles; other admins invite staff only.
  const inviteRoles: Role[] = actor?.role === 'superadmin' ? ['staff', 'instructor', 'admin'] : ['staff'];
  const invitesQuery = useInvites();
  const invites = invitesQuery.data ?? [];
  const createInvite = useCreateInvite();
  const revoke = useRevokeInvite();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<Invite | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<Invite | null>(null);
  const [filter, setFilter] = useState<'active' | 'all'>('active');
  const activeCount = invites.filter((i) => i.status === 'active').length;
  const shown = filter === 'active' ? invites.filter((i) => i.status === 'active') : invites;

  return (
    <ConsolePage
      title="Invites"
      th="คำเชิญเข้าระบบ"
      crumbs="PEOPLE"
      maxWidth={1140}
      actions={<button className="btn btn-primary btn-sm" onClick={() => { setCreated(null); setOpen(true); }}><Icon name="plus" size={15} /> New invite</button>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 8, padding: '12px 14px', background: 'var(--sage-tint)', borderRadius: 10, fontSize: 13, color: 'var(--sage-deep)' }}>
          <Icon name="shield" size={16} style={{ flex: 'none', marginTop: 1 }} />
          <span><strong>Invite-only.</strong> An invite is the only way someone joins Warm Paper ED. Redeeming a link creates an <strong>active</strong> account immediately — no separate approval step.</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Segmented value={filter} onChange={setFilter} size="sm" options={[{ value: 'active', label: `Active · ${activeCount}` }, { value: 'all', label: `All · ${invites.length}` }]} />
        </div>

        <QueryState query={invitesQuery} skeleton={<ListSkeleton rows={4} />} isEmpty={() => shown.length === 0} emptyIcon="flag" emptyTitle={filter === 'active' ? 'No active invites' : 'No invites yet'} emptyHint="Create one to bring staff into the system." emptyAction={<button className="btn btn-primary btn-sm" onClick={() => { setCreated(null); setOpen(true); }}><Icon name="plus" size={15} /> New invite</button>}>
          {() => (
            <Panel pad={false}>
              <DataTable
                cols={COLS}
                rows={shown}
                renderRow={(inv) => (
                  <>
                    <Td><span className="u-mono muted" style={{ fontSize: 12 }}>{inv.token.slice(0, 8)}…</span></Td>
                    <Td>
                      <div style={{ fontSize: 13.5, fontWeight: 500 }}>{roleDefs[inv.role].label.en}</div>
                      <div className="th muted" style={{ fontSize: 12 }}>{professions[inv.profession].label.en}</div>
                    </Td>
                    <Td><span style={{ fontSize: 13 }}>{inv.dept}</span><div className="muted" style={{ fontSize: 11.5 }}>{inv.unit}</div></Td>
                    <Td align="center"><span className="u-mono" style={{ fontSize: 13 }}>{inv.uses}/{inv.maxUses}</span></Td>
                    <Td><span className="muted" style={{ fontSize: 12.5 }}>{inv.expiresAt}</span></Td>
                    <Td><Badge kind={STATUS_BADGE[inv.status]} dot={inv.status === 'active'}>{inv.status[0].toUpperCase() + inv.status.slice(1)}</Badge></Td>
                    <Td align="center">
                      {inv.status === 'active' ? (
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '6px 10px' }} onClick={() => { setCreated(inv); setOpen(true); }}><Icon name="qr" size={14} /> Link</button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '6px 10px', color: 'var(--danger)' }} onClick={() => setConfirmRevoke(inv)}>Revoke</button>
                        </div>
                      ) : (
                        <span className="muted" style={{ fontSize: 12 }}>—</span>
                      )}
                    </Td>
                  </>
                )}
              />
            </Panel>
          )}
        </QueryState>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={created ? 'Invite created' : 'New invite'} width={created ? 420 : 480}>
        {created ? <CreatedView invite={created} onDone={() => setOpen(false)} /> : (
          <CreateForm
            roles={inviteRoles}
            pending={createInvite.isPending}
            onSubmit={(input) => createInvite.mutate(input, { onSuccess: (inv) => { setCreated(inv); toast('Invite created', { icon: 'check' }); } })}
          />
        )}
      </Modal>

      <Modal open={!!confirmRevoke} onClose={() => setConfirmRevoke(null)} title="Revoke invite?" width={380}>
        {confirmRevoke && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              Revoke this <strong>{roleDefs[confirmRevoke.role].label.en}</strong> invite? The link stops working immediately — anyone who hasn't joined yet will need a new one.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirmRevoke(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)' }}
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(confirmRevoke.id, { onSuccess: () => { toast('Invite revoked', { tone: 'clay', icon: 'x' }); setConfirmRevoke(null); } })}
              >
                Revoke
              </button>
            </div>
          </div>
        )}
      </Modal>
    </ConsolePage>
  );
}

function CreateForm({ onSubmit, pending, roles }: { onSubmit: (input: CreateInviteInput) => void; pending: boolean; roles: Role[] }) {
  const { data: org = [] } = useOrg();
  const [role, setRole] = useState<Role>('staff');
  const [profession, setProfession] = useState<Profession>('NURSE');
  const [deptId, setDeptId] = useState(org[0]?.deptId ?? '');
  const units = org.find((d) => d.deptId === deptId)?.units ?? [];
  const [unitId, setUnitId] = useState(units[0]?.id ?? '');
  const [maxUses, setMaxUses] = useState(10);
  const [expiry, setExpiry] = useState<ExpiryCode>('24h');
  const elevated = role !== 'staff';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {roles.length > 1 && (
        <div className="field">
          <span className="label">Role</span>
          <Segmented value={role} onChange={(r) => { setRole(r); if (r !== 'staff') setMaxUses(1); }} size="sm" options={roles.map((r) => ({ value: r, label: roleDefs[r].label.en }))} />
          {elevated && <span style={{ fontSize: 11.5, color: 'var(--spark-deep)' }}>Elevated roles are single-use.</span>}
        </div>
      )}

      <label className="field">
        <span className="label">Profession · วิชาชีพ</span>
        <select className="select" value={profession} onChange={(e) => setProfession(e.target.value as Profession)}>
          {PROFESSION_IDS.map((p) => <option key={p} value={p}>{professions[p].label.en} · {professions[p].label.th}</option>)}
        </select>
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>Sets the title options the invitee sees at onboarding.</span>
      </label>

      <div style={{ display: 'flex', gap: 10 }}>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">Department</span>
          <select className="select" value={deptId} onChange={(e) => { setDeptId(e.target.value); const u = org.find((d) => d.deptId === e.target.value)?.units ?? []; setUnitId(u[0]?.id ?? ''); }}>
            {org.map((d) => <option key={d.deptId} value={d.deptId}>{d.dept}</option>)}
          </select>
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">Unit</span>
          <select className="select" value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={units.length === 0}>
            {units.length === 0
              ? <option value="">ทั้งองค์กร · Whole organization</option>
              : units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">Max uses</span>
          <input className="input" type="number" min={1} value={maxUses} disabled={elevated} onChange={(e) => setMaxUses(Math.max(1, +e.target.value))} />
        </label>
        <div className="field" style={{ flex: 1 }}>
          <span className="label">Expires in</span>
          <Segmented value={expiry} onChange={setExpiry} size="sm" options={EXPIRY_OPTS} />
        </div>
      </div>

      <button className="btn btn-primary btn-block" disabled={pending || !deptId} onClick={() => onSubmit({ role, profession, dept: deptId, unit: normalizeUnit(unitId), maxUses: elevated ? 1 : maxUses, expiresAt: expiry })}>
        {pending ? 'Creating…' : 'Create invite'}
      </button>
    </div>
  );
}

function CreatedView({ invite, onDone }: { invite: Invite; onDone: () => void }) {
  const { toast } = useToast();
  const appUrl = import.meta.env.VITE_APP_URL ?? 'https://warmpaper.club';
  const link = `${appUrl}/?invite=${invite.token}`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <QR value={link} size={150} />
      <div style={{ width: '100%', display: 'flex', gap: 8, flexWrap: 'nowrap', alignItems: 'center' }}>
        <input className="input u-mono" readOnly value={link} style={{ flex: 1, minWidth: 0, fontSize: 12 }} />
        <button className="btn btn-ghost" style={{ flexShrink: 0 }} onClick={() => { navigator.clipboard?.writeText(link); toast('Link copied', { icon: 'copy' }); }}><Icon name="copy" size={16} /></button>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'center' }}>
        {roleDefs[invite.role].label.en} · {invite.dept} / {invite.unit} · expires {invite.expiresAt} · {invite.maxUses} use{invite.maxUses !== 1 ? 's' : ''}
      </div>
      <button className="btn btn-primary btn-block" onClick={onDone}>Done</button>
    </div>
  );
}
